// ─────────────────────────────────────────────────────────────────────────────
// offlineSync.js — Lightweight offline-first queue for workouts that fail to
// POST while the device is offline / on patchy water signal.
//
// Strategy:
//   1. saveWorkoutResilient(payload, postFn)
//        • Tries postFn(payload) first
//        • On network failure (TypeError "Failed to fetch", or any rejection)
//          → push payload into localStorage queue and return { queued: true }
//   2. On window 'online' event OR explicit syncQueue() call, every queued
//      payload is retried in order. Successful ones are removed.
//
// The queue lives in localStorage under `crewz_workout_outbox`. Each entry has
//   { id, payload, attempts, last_error, queued_at }
// so the user can later see how many sessions are pending sync.
// ─────────────────────────────────────────────────────────────────────────────

const QUEUE_KEY = 'crewz_workout_outbox';
const MAX_QUEUE = 100;     // hard cap to avoid runaway localStorage growth

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(0, MAX_QUEUE)));
    window.dispatchEvent(new CustomEvent('crewz-outbox-changed', { detail: { size: q.length } }));
  } catch (_e) { /* swallow */ }
}

export function getOutboxSize() {
  return readQueue().length;
}

export function getOutbox() {
  return readQueue();
}

export function clearOutbox() {
  writeQueue([]);
}

/**
 * Try posting a workout; queue it on failure so it syncs when back online.
 *
 * @param {object} payload — workout JSON
 * @param {(payload) => Promise<Response>} postFn — caller-provided POST helper.
 *   Should resolve with a Response-like { ok, status, json } object OR throw
 *   on network failure. Returning ok=false also queues (server-side 5xx).
 *
 * @returns {Promise<{ ok: boolean, queued: boolean, status?: number, error?: string }>}
 */
export async function saveWorkoutResilient(payload, postFn) {
  // Always require offline detection too. If browser says offline → queue immediately.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    enqueue(payload, 'offline at submit');
    return { ok: false, queued: true, error: 'offline' };
  }
  try {
    const res = await postFn(payload);
    if (res?.ok) {
      // Surface the saved workout back to callers so the toast can
      // show an ID and a "View in History" link — iter-68 diagnostic
      // for the Mode 3 "session saved but not in history" report.
      let body = null;
      try { body = await res.json(); } catch { /* body already read or empty */ }
      return { ok: true, queued: false, status: res.status, body };
    }
    // Session expired (e.g. rowed offline for a long time) → keep it for after sign-in
    if (res?.status === 401 || res?.status === 403) {
      enqueue(payload, `HTTP ${res.status} (sign in to sync)`);
      return { ok: false, queued: true, status: res.status, error: 'Signed out — saved on this device, will sync after sign-in' };
    }
    // 4xx is a bug, not a network blip — don't queue
    if (res?.status >= 400 && res?.status < 500) {
      let errBody = null;
      try { errBody = await res.text(); } catch { /* ignore */ }
      return { ok: false, queued: false, status: res.status, error: `HTTP ${res.status}${errBody ? ' — ' + errBody.slice(0, 200) : ''}` };
    }
    // 5xx or unknown → queue for retry
    enqueue(payload, `HTTP ${res?.status || '???'}`);
    return { ok: false, queued: true, status: res?.status, error: `HTTP ${res?.status} — queued for retry` };
  } catch (err) {
    enqueue(payload, err?.message || 'network error');
    return { ok: false, queued: true, error: err?.message || 'network error' };
  }
}

function enqueue(payload, reason) {
  const q = readQueue();
  q.push({
    id: `wo_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    payload,
    attempts: 0,
    last_error: reason || null,
    queued_at: Date.now(),
  });
  writeQueue(q);
}

/**
 * Retry every queued workout. Successful ones are removed.
 * Failures stay in the queue with an incremented attempts counter.
 */
// Concurrency guard — prevents overlapping syncQueue runs from double-posting
// the same outbox items. React StrictMode dev double-mount, back/forward nav,
// or an 'online' event colliding with a manual flush can all trigger this.
let _syncInFlight = false;

export async function syncQueue(postFn) {
  if (_syncInFlight) return { synced: 0, remaining: readQueue().length, skipped: true };
  _syncInFlight = true;
  try {
    const q = readQueue();
    if (q.length === 0) return { synced: 0, remaining: 0 };
    const remaining = [];
    let synced = 0;
    for (const entry of q) {
      try {
        const res = await postFn(entry.payload);
        if (res?.ok) {
          synced += 1;
        } else if (res?.status === 401 || res?.status === 403) {
          // Signed out while offline — keep it until the rower signs back in
          remaining.push({ ...entry, attempts: entry.attempts + 1, last_error: `HTTP ${res.status} (sign in to sync)` });
        } else if (res?.status >= 400 && res?.status < 500) {
          // 4xx — drop it; nothing the queue can fix
          synced += 1;
        } else {
          remaining.push({ ...entry, attempts: entry.attempts + 1, last_error: `HTTP ${res?.status || '???'}` });
        }
      } catch (err) {
        remaining.push({ ...entry, attempts: entry.attempts + 1, last_error: err?.message || 'network error' });
      }
    }
    writeQueue(remaining);
    return { synced, remaining: remaining.length };
  } finally {
    _syncInFlight = false;
  }
}

/**
 * Wire up automatic sync-on-online. Returns an unsubscribe fn.
 */
export function startOnlineAutoSync(postFn) {
  if (typeof window === 'undefined') return () => {};
  const handler = () => { syncQueue(postFn); };
  window.addEventListener('online', handler);
  // Try once on startup in case the user is already online with a backlog
  if (navigator.onLine !== false) handler();
  return () => window.removeEventListener('online', handler);
}
